// LanguageContext — keeps i18next language in sync with localStorage + backend user prefs.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { applyDir, persistLang } from "../i18n";
import { api, getToken } from "../api/client";

const LanguageContext = createContext(null);

export const LANGUAGES = [
    { code: "en", name: "English", native_name: "English", flag_emoji: "🇬🇧", rtl: false },
    { code: "hi", name: "Hindi", native_name: "हिन्दी", flag_emoji: "🇮🇳", rtl: false },
    { code: "te", name: "Telugu", native_name: "తెలుగు", flag_emoji: "🇮🇳", rtl: false },
    { code: "ta", name: "Tamil", native_name: "தமிழ்", flag_emoji: "🇮🇳", rtl: false },
    { code: "ml", name: "Malayalam", native_name: "മലയാളം", flag_emoji: "🇮🇳", rtl: false },
    { code: "kn", name: "Kannada", native_name: "ಕನ್ನಡ", flag_emoji: "🇮🇳", rtl: false },
    { code: "mr", name: "Marathi", native_name: "मराठी", flag_emoji: "🇮🇳", rtl: false },
    { code: "or", name: "Odia", native_name: "ଓଡ଼ିଆ", flag_emoji: "🇮🇳", rtl: false },
    { code: "bn", name: "Bengali", native_name: "বাংলা", flag_emoji: "🇮🇳", rtl: false },
    { code: "gu", name: "Gujarati", native_name: "ગુજરાતી", flag_emoji: "🇮🇳", rtl: false },
    { code: "ur", name: "Urdu", native_name: "اردو", flag_emoji: "🇵🇰", rtl: true },
    { code: "pa", name: "Punjabi", native_name: "ਪੰਜਾਬੀ", flag_emoji: "🇮🇳", rtl: false },
    { code: "as", name: "Assamese", native_name: "অসমীয়া", flag_emoji: "🇮🇳", rtl: false },
];

export const LanguageProvider = ({ children }) => {
    const { i18n: i18nInstance } = useTranslation();
    const [lang, setLangState] = useState(i18nInstance.language || "en");

    useEffect(() => {
        const onLangChanged = (lng) => {
            setLangState(lng);
            applyDir(lng);
        };
        i18nInstance.on("languageChanged", onLangChanged);
        applyDir(lang);
        return () => i18nInstance.off("languageChanged", onLangChanged);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // On mount, if logged in, fetch user prefs and apply (if different)
    useEffect(() => {
        if (!getToken()) return;
        let cancelled = false;
        api.get("/auth/me").then((r) => {
            if (cancelled) return;
            const preferred = r?.data?.preferences?.language;
            if (preferred && preferred !== lang && LANGUAGES.find((l) => l.code === preferred)) {
                i18nInstance.changeLanguage(preferred);
                persistLang(preferred);
            }
        }).catch(() => { /* ignore */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setLang = useCallback(async (newLang) => {
        if (!LANGUAGES.find((l) => l.code === newLang)) return;
        await i18nInstance.changeLanguage(newLang);
        persistLang(newLang);
        // Save backend pref if logged in (fire-and-forget)
        if (getToken()) {
            api.patch("/auth/me", { preferences: { language: newLang } }).catch(() => { /* ignore */ });
        }
    }, [i18nInstance]);

    // v3D — Watch the i18n cache version and reload translations when admin edits land.
    // Polls every 60s and on tab focus. Cheap (~50 bytes per request).
    useEffect(() => {
        if (lang === "en") return; // English has no translations to reload
        let cancelled = false;
        let lastVersion = null;
        const STORAGE_KEY = `pills.i18n.version.${lang}.ui`;
        try { lastVersion = window.localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }

        const check = async () => {
            try {
                const { data } = await api.get("/i18n/version", { params: { lang, namespace: "ui" } });
                if (cancelled) return;
                const v = String(data.version);
                if (lastVersion && lastVersion !== v) {
                    await i18nInstance.reloadResources([lang], ["ui"]);
                }
                lastVersion = v;
                try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
            } catch { /* ignore */ }
        };

        check();
        const id = setInterval(check, 60_000);
        const onFocus = () => check();
        window.addEventListener("focus", onFocus);
        return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, [lang, i18nInstance]);

    const value = useMemo(() => ({
        lang,
        setLang,
        languages: LANGUAGES,
        isRTL: LANGUAGES.find((l) => l.code === lang)?.rtl || false,
    }), [lang, setLang]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext) || { lang: "en", setLang: () => {}, languages: LANGUAGES, isRTL: false };
